// Phase 26 special shapes (mirrors backend special.py): outline polygon in inches, unit-local, y down.
export const SPECIAL = [
  ["trapezoid", "Trapezoid", [["top_w", "Top width"], ["offset", "Top offset from left"]]],
  ["triangle", "Triangle", [["peak_x", "Peak from left"]]],
  ["pentagon", "Pentagon", [["side_h", "Side height"]]],
  ["hexagon", "Hexagon", [["cut", "Point depth"]]],
  ["diamond", "Diamond", []],
  ["right_triangle", "Right Triangle", [["side", "Tall side (left/right)"]]],
  ["parallelogram", "Parallelogram", [["offset", "Lean"]]],
  ["gable", "Gable", [["side_h", "Eave height"]]],
  ["sloped_top", "Sloped Top", [["left_h", "Left height"], ["right_h", "Right height"]]],
  ["double_sloped", "Double Sloped", [["side_h", "Side height"], ["peak_x", "Peak from left"]]],
  ["octagon", "Octagon", [["cut", "Corner cut"]]],
  ["custom_trapezoid", "Custom Trapezoid", [["tl_h", "Top-left height"], ["tl_x", "Top-left inset"], ["tr_h", "Top-right height"], ["tr_x", "Top-right inset"],
    ["bl_x", "Bottom-left inset"], ["br_x", "Bottom-right inset"]]],
  ["l_shape", "L-Shape", [["arm_w", "Tall leg width"], ["arm_h", "Foot height"]]],
  ["t_shape", "T-Shape", [["arm_w", "Stem width"], ["arm_h", "Top bar height"]]],
  ["cross", "Cross", [["arm_w", "Vertical bar width"], ["arm_h", "Horizontal bar height"]]],
];
export const specialName = (k) => SPECIAL.find(([s]) => s === k)?.[1] || "";

const num = (p, k, d, lo, hi) => { const v = p[k] === undefined || p[k] === "" ? d : Number(p[k]); return Math.min(Math.max(Number.isFinite(v) ? v : d, lo), hi); };

export function polygon(sp, w, h) {
  const k = sp?.shape, p = sp || {};
  if (!k || !(w > 0) || !(h > 0)) return [];
  switch (k) {
    case "trapezoid": { const tw = num(p, "top_w", w * 0.6, 1, w), off = num(p, "offset", (w - tw) / 2, 0, w - tw); return [[off, 0], [off + tw, 0], [w, h], [0, h]]; }
    case "triangle": { const px = num(p, "peak_x", w / 2, 0, w); return [[px, 0], [w, h], [0, h]]; }
    case "pentagon": case "gable": { const sh = num(p, "side_h", h * (k === "pentagon" ? 0.62 : 0.78), 1, h); return [[0, h], [0, h - sh], [w / 2, 0], [w, h - sh], [w, h]]; }
    case "double_sloped": { const sh = num(p, "side_h", h * 0.7, 1, h), px = num(p, "peak_x", w * 0.35, 0, w); return [[0, h], [0, h - sh], [px, 0], [w, h - sh], [w, h]]; }
    case "hexagon": { const c = num(p, "cut", w * 0.25, 0, w / 2); return [[c, 0], [w - c, 0], [w, h / 2], [w - c, h], [c, h], [0, h / 2]]; }
    case "diamond": return [[w / 2, 0], [w, h / 2], [w / 2, h], [0, h / 2]];
    case "right_triangle": return p.side === "right" ? [[w, 0], [w, h], [0, h]] : [[0, 0], [w, h], [0, h]];
    case "parallelogram": { const o = num(p, "offset", w * 0.2, 0, w * 0.9); return [[o, 0], [w, 0], [w - o, h], [0, h]]; }
    case "sloped_top": { const lh = num(p, "left_h", h, 1, h), rh = num(p, "right_h", h * 0.6, 1, h); return [[0, h - lh], [w, h - rh], [w, h], [0, h]]; }
    case "octagon": { const c = num(p, "cut", Math.min(w, h) * 0.29, 0, Math.min(w, h) / 2); return [[c, 0], [w - c, 0], [w, c], [w, h - c], [w - c, h], [c, h], [0, h - c], [0, c]]; }
    case "custom_trapezoid": return [[num(p, "tl_x", 0, 0, w), h - num(p, "tl_h", h * 0.7, 1, h)], [w - num(p, "tr_x", 0, 0, w), h - num(p, "tr_h", h, 1, h)],
      [w - num(p, "br_x", 0, 0, w), h], [num(p, "bl_x", 0, 0, w), h]];
    case "l_shape": { const a = num(p, "arm_w", w * 0.4, 1, w), b = num(p, "arm_h", h * 0.4, 1, h); return [[0, 0], [a, 0], [a, h - b], [w, h - b], [w, h], [0, h]]; }
    case "t_shape": { const a = num(p, "arm_w", w * 0.4, 1, w), b = num(p, "arm_h", h * 0.35, 1, h); return [[0, 0], [w, 0], [w, b], [(w + a) / 2, b], [(w + a) / 2, h], [(w - a) / 2, h], [(w - a) / 2, b], [0, b]]; }
    case "cross": {
      const a = num(p, "arm_w", w * 0.34, 1, w), b = num(p, "arm_h", h * 0.34, 1, h), xa = (w - a) / 2, xb = (w + a) / 2, ya = (h - b) / 2, yb = (h + b) / 2;
      return [[xa, 0], [xb, 0], [xb, ya], [w, ya], [w, yb], [xb, yb], [xb, h], [xa, h], [xa, yb], [0, yb], [0, ya], [xa, ya]];
    }
    default: return [];
  }
}

// [top, bottom] y of the outline at x (one interval per vertical line for every shape).
export function span(poly, x) {
  const ys = [];
  poly.forEach(([x1, y1], i) => {
    const [x2, y2] = poly[(i + 1) % poly.length];
    if (x1 === x2) { if (Math.abs(x - x1) < 1e-9) ys.push(y1, y2); return; }
    if (x >= Math.min(x1, x2) - 1e-9 && x <= Math.max(x1, x2) + 1e-9) ys.push(y1 + ((y2 - y1) * (x - x1)) / (x2 - x1));
  });
  return ys.length ? [Math.min(...ys), Math.max(...ys)] : null;
}

export const polyPath = (poly, dx = 0, dy = 0) => (poly.length ? `M ${poly.map(([x, y]) => `${x + dx} ${y + dy}`).join(" L ")} Z` : "");
